<template>
  <div
    class="page-content-slider lg:h-125 flex items-center justify-between gap-5 max-lg:flex-col!"
    :class="ui?.root"
  >
    <SPSkeleton
      v-show="loading"
      variant="card"
      :ui="{
        card: 'w-full!',
      }"
    />

    <UCard
      v-show="!loading"
      class="content relative h-full shrink-0 ring-0 rounded-[24px] w-103.25! max-xl:max-w-full max-lg:w-full!"
      :class="ui?.contentCard"
      :ui="{
        body: ui?.contentCardBody,
      }"
    >
      <slot name="content">
        <slot name="title">
          <h2
            class="mb-5 text-size-h2 leading-10 font-semibold"
            :class="ui?.title"
          >
            {{ title }}
          </h2>
        </slot>

        <slot v-if="locationBadge" name="badge">
          <ClientOnly>
            <LocationBadge
              v-if="selectedCity"
              class="mb-4 bg-background-bg1 text-sm"
              :class="ui?.badge"
            >
              {{ selectedCity?.label }}
            </LocationBadge>
          </ClientOnly>
        </slot>

        <slot name="description">
          <p
            class="description mb-10 line-clamp-2 typo-paragraph-md-regular text-content-default-secondary max-sm:leading-h1-paragraph"
            :class="ui?.description"
          >
            {{ description }}
          </p>
        </slot>

        <slot v-if="$slots['counter'] || counter" name="counter">
          <div v-if="counter" class="counter" :class="ui?.counter">
            <div
              class="inline-flex items-center gap-2 typo-heading-h2 text-accent-default"
            >
              <span class="-translate-y-0.5">&gt;</span>
              <span :class="ui?.counterAmount">
                {{ counter.amount }}
              </span>
            </div>
            <p class="counter-text" :class="ui?.counterText">
              {{ counter.text }}
            </p>
          </div>
        </slot>

        <slot v-if="$slots['image'] || image" name="image">
          <img
            v-if="image"
            class="image"
            :class="ui?.image"
            :src="image"
            alt="Акцентное изображение блока"
          />
        </slot>
      </slot>

      <div v-if="$slots['card-footer']" class="mt-auto">
        <slot name="card-footer" />
      </div>
    </UCard>

    <SPSkeleton
      v-show="loading || !slides.length"
      class="w-full"
      variant="slider"
    />

    <div
      v-show="!loading && slides.length"
      class="slider flex h-full min-w-0 w-full flex-col overflow-hidden"
      :class="[
        {
          'is-translated': isTranslated,
        },
        ui?.slider,
      ]"
    >
      <Swiper
        class="min-h-0 flex-1 grow overflow-hidden w-full shrink-0"
        :modules="[Grid]"
        :space-between="16"
        :slides-per-view="1.1"
        :grid="{ rows: 1 }"
        :breakpoints="{
          1024: {
            slidesPerView: 1,
          },
        }"
        @swiper="onSwiperInit"
        @set-translate="translateHandler"
        @slide-change="slideChangeHandler"
      >
        <SwiperSlide
          v-for="slide in slides"
          :key="slide?.id"
          class="h-full w-full bg-cover bg-no-repeat p-5 rounded-[24px] max-sm:h-78! max-sm:flex! max-sm:items-end!"
          :class="ui?.slide"
          :style="{
            backgroundImage: slide?.image_url
              ? `url(${slide.image_url})`
              : undefined,
          }"
        >
          <slot name="slide" :slide="slide">
            <FeaturedCard
              class="h-full! w-95! max-lg:w-105! rounded-[24px]"
              :class="ui?.featuredCard"
              :data="slide"
              :title="slideTitle(slide)"
              :description="slideDescription(slide)"
              :to="`${toPrefix}/${slide.id}`"
              :ui="{
                description: [
                  'line-clamp-2 overflow-hidden',
                  ui?.featuredCardDescription,
                ].join(' '),
                info: ui?.featuredCardInfo,
                title: ui?.featuredCardTitle,
                badge: ui?.featuredCardBadge,
              }"
            >
              <template #slide-badges>
                <slot name="slide-badges" v-bind="{ slide }" />
              </template>

              <template #button>
                <slot name="slide-button" :slide="slide" />
              </template>
            </FeaturedCard>
          </slot>
        </SwiperSlide>
      </Swiper>

      <div
        class="slider-controls flex shrink items-center gap-6 p-3.25 grow-0 h-8 mt-3 px-0 mb-px max-sm:mt-0.75"
        :class="ui?.sliderControls"
      >
        <div
          class="scrollbar-track relative h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-container-default"
          :class="ui?.scrollbarTrack"
        >
          <div
            class="h-full rounded-full bg-accent-default transition-all duration-300"
            :class="ui?.scrollbarFill"
            :style="{ width: `${((currentIndex + 1) / slides.length) * 100}%` }"
          />
        </div>

        <div class="buttons flex items-center gap-3 max-lg:hidden">
          <UButton
            class="text-button-secondary-foreground"
            :disabled="swiperInstance?.isBeginning"
            icon="i-local-chevron-left"
            :variant="swiperInstance?.isBeginning ? 'outline' : 'solid'"
            :ui="{
              leadingIcon: !swiperInstance?.isBeginning ? 'text-white' : '',
            }"
            @click="void swiperInstance?.slidePrev()"
          />

          <span class="text-sm text-neutral-default text-[16px]">
            {{ currentIndex + 1 }} / {{ slides.length }}
          </span>

          <ClientOnly>
            <UButton
              :loading="loading"
              :disabled="swiperInstance?.isEnd"
              icon="i-local-chevron-right"
              color="primary"
              :variant="swiperInstance?.isEnd ? 'soft' : 'solid'"
              :ui="{
                leadingIcon: 'text-white',
              }"
              @click="void swiperInstance?.slideNext()"
            />
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup generic="T extends CardData = CardData">
import type { SwiperClass } from 'swiper/react'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Grid } from 'swiper/modules'

import FeaturedCard from './FeaturedCard.vue'
import type { CardData } from '~/types/index.js'

const swiperInstance = ref<SwiperClass | null>(null)
const currentIndex = ref(0)

const onSwiperInit = (swiper: SwiperClass) => {
  swiperInstance.value = swiper
  nextTick(() => swiper.update())
}

defineOptions({
  name: 'PageContentSlider',
})

withDefaults(
  defineProps<{
    title: string
    description?: string
    toPrefix?: string
    image?: string
    counter?: {
      amount: number | string
      text: string
    }
    slides: T[]
    loading?: boolean
    locationBadge?: boolean
    ui?: {
      root?: string
      contentCard?: string
      contentCardBody?: string
      contentCardDescription?: string
      title?: string
      badge?: string
      description?: string
      counter?: string
      counterAmount?: string
      counterText?: string
      image?: string
      slider?: string
      slide?: string
      featuredCard?: string
      featuredCardInfo?: string
      featuredCardTitle?: string
      featuredCardDescription?: string
      featuredCardBadge?: string
      sliderControls?: string
      scrollbarTrack?: string
      scrollbarFill?: string
    }
  }>(),
  {
    locationBadge: false,
  },
)

const directoryStore = useDirectoryStore()
const { selectedCity } = storeToRefs(directoryStore)

const isTranslated = ref(false)

const slideTitle = (slide?: T) => {
  if (!slide) return ''
  return ('title' in slide ? slide.title : slide.name) || ''
}

const slideDescription = (slide?: T) => {
  if (!slide) return ''
  if ('description' in slide) return (slide as any).description || ''
  if ('preview_description' in slide)
    return (slide as any).preview_description || ''
  return ''
}

const slideChangeHandler = () => {
  currentIndex.value = swiperInstance.value?.activeIndex ?? 0
}

const translateHandler = (_: any, translate: number) => {
  isTranslated.value = !!translate
}
</script>

<style lang="scss" scoped>
.content {
  width: 373px;
}

.description {
  font-family: Lato, sans-serif;
}

.counter {
  display: flex;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 160px;
  height: 108px;
  min-width: max-content;
  border-radius: 16px;
  border: 1px solid var(--color-neutral-border-default);
  background: var(--color-neutral-container-muted);

  @include max-xl {
    height: 106px;
  }

  @include max-md {
    height: 96px;
  }
}

.counter-text {
  @include typo('paragraph-md-regular');
  font-family: var(--font-lato);
  color: var(--color-accent-default, #0069ff);
}

.slider {
  @include max-lg {
    width: calc(100% + 40px);
    margin-right: -40px;
  }

  @include max-md {
    width: calc(100% + 16px);
    margin-right: -16px;
  }

  &.is-translated {
    @include max-lg {
      margin-left: -40px;
      width: calc(100% + 40px * 2);

      .slider-controls {
        padding-left: 40px;
      }
    }

    @include max-md {
      margin-left: -16px;
      width: calc(100% + 16px * 2);

      .slider-controls {
        padding-left: 16px;
      }
    }
  }
}

.slider-controls {
  @include max-lg {
    padding-right: 40px;
  }

  @include max-md {
    padding-right: 16px;
  }
}

.image {
  position: absolute;
  bottom: -70px;
  right: -50px;
  width: 330px;
  height: 280px;
  transform: rotate(24.641deg);
  z-index: 1;
  object-fit: contain;

  @include max-xl {
    bottom: -50px;
    right: -10px;
  }

  @include max-lg {
    bottom: -100px;
    right: 100px;
  }

  @include max-md {
    bottom: -100px;
    right: -50px;
    scale: 60%;
  }
}

:deep(.swiper-wrapper) {
  height: 100%;
}

:deep(.swiper-slide) {
  height: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  @include max-lg {
    height: 370px;
  }
}
</style>
